<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";
import WrIcon from "~/components/global/WrIcon.vue";
import WrSVGIcon from "~/components/global/WrSVGIcon.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

interface WhyItMattersCard {
  title: string;
  description: string;
  icon?: string;
  svgIcon?: string;
}

const emit = defineEmits<{ "cta-clicked": [] }>();

const props = withDefaults(
  defineProps<{
    heading?: string;
    subcopy?: string;
    cards?: WhyItMattersCard[];
    ctaText?: string;
    ctaLink?: string | null;
  }>(),
  {
    heading: "Why It Matters",
    subcopy: "",
    cards: () => [],
    ctaText: "Get Started",
    ctaLink: null,
  },
);
</script>

<template>
  <section
    class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]"
    aria-label="Why It Matters"
  >
    <h2
      class="text-[28px] lg:text-[48px] font-semibold leading-tight text-center font-avant-demi"
      style="
        background: linear-gradient(92deg, #12dbff -3.57%, #ba55fe 80.72%);
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      "
    >
      {{ heading }}
    </h2>
    <p
      class="text-[16px] lg:text-[18px] font-normal tracking-[0.36px] mt-4 px-4 text-center max-w-[720px] mx-auto font-avant"
      :class="isLight ? 'text-black' : 'text-white'"
    >
      {{ subcopy }}
    </p>
    <div class="flex flex-col items-center mt-10 gap-6">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-[1100px] mx-auto px-4">
        <div
          v-for="card in cards"
          :key="card.title"
          class="flex flex-col items-start justify-start pt-9 pb-4 px-4 min-h-[206px]"
          :style="
            isLight
              ? 'border-bottom:1px solid #491C7F;background:linear-gradient(180deg,rgba(210,144,255,0) 0%,rgba(210,144,255,0.2) 100%)'
              : 'border-bottom:1px solid #491C7F;background:linear-gradient(180deg,rgba(50,11,78,0) 0%,rgba(70,14,109,0.3) 100%)'
          "
        >
          <div class="w-[50px] h-[50px] flex-shrink-0">
            <WrIcon
              v-if="card.svgIcon"
              :name="card.svgIcon"
              class="max-sm:size-6 size-8 dark:text-white text-black"
            />
            <WrSVGIcon
              v-else-if="card.icon"
              :name="card.icon"
              class="size-[50px] dark:text-white text-black"
            />
            <img
              v-else
              :src="
                isLight
                  ? (card.icon?.replace('.svg', '-light.svg') ?? card.icon)
                  : card.icon
              "
              :alt="card.title"
              width="50"
              height="50"
              class="w-full h-full"
            />
          </div>
          <h3
            class="mt-[6px] font-avant text-[20px] leading-tight font-normal"
            :class="isLight ? 'text-[#000]' : 'text-[#BCB2C3]'"
          >
            {{ card.title }}
          </h3>
          <p
            class="mt-2 text-left font-avant w-full text-[15px] font-normal tracking-[0.3px] text-[#AF66E4]"
          >
            {{ card.description }}
          </p>
        </div>
      </div>
    </div>
    <div class="flex justify-center mt-8 lg:mt-12">
      <WrButton
        @click="!ctaLink ? emit('cta-clicked') : undefined"
        :link="ctaLink || undefined"
        container-class="h-[40px] lg:h-[48px]"
        append-class="text-sm lg:font-normal px-3 lg:px-5"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </section>
</template>
